import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { Title } from 'react-admin';

type DailyPoint = { date: string; value: number };
type CountryPoint = { country: string; views: number };

const StatCard = ({ label, value }: { label: string; value: string | number }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1, fontWeight: 700 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

export const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState(0);
  const [withdrawals, setWithdrawals] = useState(0);
  const [payouts, setPayouts] = useState(0);
  const [adsWatched, setAdsWatched] = useState(0);
  const [topCountries, setTopCountries] = useState<CountryPoint[]>([]);
  const [adsByDay, setAdsByDay] = useState<DailyPoint[]>([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const usersSnap = await getDocs(collection(db, 'users'));
      const withdrawalSnap = await getDocs(collection(db, 'withdrawals'));
      const payoutSnap = await getDocs(collection(db, 'payouts'));
      const rewardsSnap = await getDocs(collection(db, 'ad_rewards'));

      setUsers(usersSnap.size);
      setWithdrawals(withdrawalSnap.size);
      setPayouts(payoutSnap.size);
      setAdsWatched(rewardsSnap.size);

      const countryMap = new Map<string, number>();
      const dayMap = new Map<string, number>();

      rewardsSnap.forEach((doc) => {
        const data = doc.data() as any;
        const country = (data.country || data.countryCode || 'Unknown') as string;
        countryMap.set(country, (countryMap.get(country) || 0) + 1);

        const timestamp = data.createdAt?.toDate?.() ?? null;
        if (timestamp) {
          const day = timestamp.toISOString().slice(0, 10);
          dayMap.set(day, (dayMap.get(day) || 0) + 1);
        }
      });

      const top = Array.from(countryMap.entries())
        .map(([country, views]) => ({ country, views }))
        .sort((a, b) => b.views - a.views)
        .slice(0, 6);
      setTopCountries(top);

      const byDay = Array.from(dayMap.entries())
        .map(([date, value]) => ({ date, value }))
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(-14);
      setAdsByDay(byDay);

      setLoading(false);
    };

    load();
  }, []);

  const maxDaily = useMemo(
    () => Math.max(...adsByDay.map((entry) => entry.value), 1),
    [adsByDay]
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Title title="Dashboard" />
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Metrics Overview
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><StatCard label="Total Users" value={users} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard label="Ads Watched" value={adsWatched} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard label="Withdrawals" value={withdrawals} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard label="Payout Records" value={payouts} /></Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Ads Watched (Last 14 Days)</Typography>
              <Stack spacing={1}>
                {adsByDay.map((entry) => (
                  <Box key={entry.date}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">{entry.date}</Typography>
                      <Typography variant="body2" fontWeight={700}>{entry.value}</Typography>
                    </Stack>
                    <Box sx={{ mt: 0.5, height: 8, borderRadius: 4, bgcolor: 'grey.800' }}>
                      <Box sx={{ height: '100%', width: `${(entry.value / maxDaily) * 100}%`, borderRadius: 4, bgcolor: 'primary.main' }} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Top Countries</Typography>
              <Stack spacing={1.5}>
                {topCountries.map((entry) => (
                  <Stack key={entry.country} direction="row" justifyContent="space-between">
                    <Typography>{entry.country}</Typography>
                    <Typography fontWeight={700}>{entry.views}</Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
